import { useState, useEffect } from "react"
import PropTypes from "prop-types"

import SharedMarkdown from "shared/components/shared_markdown"
import classnames from "classnames"

import styles from "./selected_service_view.module.scss"

import Spinner from "shared/components/spinner"
import ErrorBoundary from "shared/components/error_boundary"

import StepList from "./step_list"
import ArrowIcon from "./art/arrow_icon"
import OpenNewTabIcon from "./art/open_new_tab_icon"

import { loadServiceSteps } from "./gql"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"

const SelectedServiceView = ({
  service,
  selectedService,
  urls,
  showRemoveLink,
}) => {
  const [loaded, updateLoaded] = useState(false)
  const [steps, updateSteps] = useState({})
  const [selectedStep, viewStep] = useState("triggers")

  useEffect(() => {
    loadServiceSteps(selectedService)
      .then(data => {
        updateSteps({
          triggers: data.triggers,
          actions: data.actions,
          queries: data.queries,
        })
        viewStep("triggers") // update to first not empty
        updateLoaded(true)
      })
      .catch(rejectedXHRPromiseLogger())
  }, [])

  return (
    <div className={styles["selected-service-view-container"]}>
      <div className={styles["connection-info"]}>
        <div className={styles["service-to-service"]}>
          <img
            src={urls.serviceMonochromeIcon}
            title={service.name}
            alt={service.name}
          />
          <span>{service.name}</span>
        </div>
        <div className={styles.arrow}>
          <ArrowIcon title="connection to" />
        </div>
        <div className={styles["service-to-service"]}>
          <img
            src={urls.selectedServiceMonochromeIcon}
            title={selectedService.name}
            alt={selectedService.name}
          />
          <span>{selectedService.name}</span>
        </div>

        {showRemoveLink && (
          <div className={styles["remove-action"]}>
            <a
              className={styles["action-link"]}
              href={urls.startScreenURL}
              title={`Remove ${selectedService.name} and choose another service`}
              data-open-in-current-window={false}
            >
              Remove
            </a>
          </div>
        )}
      </div>
      <div className={styles["service-details-container"]}>
        <div
          className={styles["service-icon-container"]}
          style={{
            backgroundColor: "#" + selectedService.brand_color,
          }}
        >
          <img
            src={
              selectedService.lrg_monochrome_image_url ||
              selectedService.lrg_variant_image_url
            }
            title={selectedService.name}
            alt={selectedService.name}
          />
        </div>
        <div className={styles["service-details"]}>
          <h2>{selectedService.name}</h2>
          <a
            className={styles["action-link"]}
            href={urls.serviceURL}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${urls.serviceURL} in a new tab`}
          >
            <span>
              <span>View on IFTTT.com</span>
              <OpenNewTabIcon title="View on IFTTT.com" />
            </span>
          </a>
          <div>
            <SharedMarkdown source={selectedService.description} />
          </div>
        </div>
      </div>
      <div className={styles["service-steps"]}>
        <ul className={styles["step-type-selector"]}>
          <li onClick={() => viewStep("triggers")}>
            <div
              className={classnames({
                [styles.active]: selectedStep === "triggers",
              })}
              title="Triggers"
              aria-label="Triggers"
            >
              Triggers
            </div>
          </li>
          <li onClick={() => viewStep("queries")}>
            <div
              className={classnames({
                [styles.active]: selectedStep === "queries",
              })}
              title="Queries"
              aria-label="Queries"
            >
              Queries
            </div>
          </li>
          <li onClick={() => viewStep("actions")}>
            <div
              className={classnames({
                [styles.active]: selectedStep === "actions",
              })}
              title="Actions"
              aria-label="Actions"
            >
              Actions
            </div>
          </li>
        </ul>
        <div className={styles["step-type-details"]}>
          <ErrorBoundary>
            {loaded ? (
              <StepList stepType={selectedStep} steps={steps} />
            ) : (
              <div className={styles.spinner}>
                <Spinner
                  config={{ length: 6, width: 4, radius: 20, corners: 0 }}
                />
              </div>
            )}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}

SelectedServiceView.propTypes = {
  service: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    brand_color: PropTypes.string.isRequired,
  }),
  selectedService: PropTypes.shape({
    brand_color: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    lrg_monochrome_image_url: PropTypes.string.isRequired,
    lrg_variant_image_url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }),
  urls: PropTypes.shape({
    serviceMonochromeIcon: PropTypes.string.isRequired,
    selectedServiceMonochromeIcon: PropTypes.string.isRequired,
    serviceURL: PropTypes.string.isRequired,
    startScreenURL: PropTypes.string.isRequired,
  }),
  showRemoveLink: PropTypes.bool,
}

SelectedServiceView.defaultProps = {
  showRemoveLink: true,
}

export default SelectedServiceView
